/**
 * UTM Parameter Tracker
 * Captures and stores UTM parameters from email links for conversion attribution
 */

import { supabase } from '@/lib/supabase';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/**
 * Extract UTM parameters from URL
 */
export function extractUTMParams(url: string = window.location.href): UTMParams | null {
  try {
    const urlObj = new URL(url);
    const params: UTMParams = {};
    let hasUTM = false;

    // Extract each UTM parameter
    const utmSource = urlObj.searchParams.get('utm_source');
    const utmMedium = urlObj.searchParams.get('utm_medium');
    const utmCampaign = urlObj.searchParams.get('utm_campaign');
    const utmContent = urlObj.searchParams.get('utm_content');
    const utmTerm = urlObj.searchParams.get('utm_term');

    if (utmSource) {
      params.utm_source = utmSource;
      hasUTM = true;
    }
    if (utmMedium) {
      params.utm_medium = utmMedium;
      hasUTM = true;
    }
    if (utmCampaign) {
      params.utm_campaign = utmCampaign;
      hasUTM = true;
    }
    if (utmContent) {
      params.utm_content = utmContent;
      hasUTM = true;
    }
    if (utmTerm) {
      params.utm_term = utmTerm;
      hasUTM = true;
    }

    return hasUTM ? params : null;
  } catch (error) {
    console.error('Error extracting UTM params:', error);
    return null;
  }
}

/**
 * Store UTM parameters in session storage for attribution
 */
export function storeUTMParams(params: UTMParams): void {
  try {
    sessionStorage.setItem('utm_params', JSON.stringify(params));
    console.log('📍 Stored UTM params:', params);
  } catch (error) {
    console.error('Error storing UTM params:', error);
  }
}

/**
 * Get stored UTM parameters
 */
export function getStoredUTMParams(): UTMParams | null {
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error getting stored UTM params:', error);
    return null;
  }
}

/**
 * Track UTM click event to analytics
 */
export async function trackUTMClick(userId: string, params: UTMParams, pageUrl: string): Promise<void> {
  try {
    // Track to analytics_events
    await supabase.from('analytics_events').insert({
      user_id: userId,
      event_name: 'email_link_clicked',
      event_category: 'email',
      page_url: pageUrl,
      properties: {
        ...params,
        timestamp: new Date().toISOString(),
      },
      timestamp: new Date().toISOString(),
    });

    console.log('✅ Tracked UTM click event:', params);
  } catch (error) {
    console.error('Error tracking UTM click:', error);
  }
}

/**
 * Track conversion with UTM attribution
 */
export async function trackConversionWithUTM(
  userId: string,
  conversionType: string
): Promise<void> {
  try {
    const utmParams = getStoredUTMParams();

    if (!utmParams) {
      console.log('No UTM params found for conversion attribution');
      return;
    }

    // If this is an email-driven conversion, record it in email_conversions
    if (utmParams.utm_source === 'email' && utmParams.utm_content) {
      // Get variant_id from user_email_assignments
      const emailType = mapUTMMediumToTemplateName(utmParams.utm_medium || '');

      if (emailType) {
        const { data: assignment } = await supabase
          .from('user_email_assignments')
          .select('variant_id')
          .eq('user_id', userId)
          .eq('email_type', emailType)
          .single();

        if (assignment?.variant_id) {
          // Record conversion in email_conversions table
          await supabase.from('email_conversions').insert({
            variant_id: assignment.variant_id,
            user_id: userId,
            conversion_type: conversionType,
            utm_params: utmParams,
          });

          console.log('✅ Tracked email conversion:', {
            variant_id: assignment.variant_id,
            conversion_type: conversionType,
          });
        }
      }
    }

    // Also track general conversion event
    await supabase.from('analytics_events').insert({
      user_id: userId,
      event_name: `conversion_${conversionType}`,
      event_category: 'conversion',
      properties: {
        ...utmParams,
        conversion_type: conversionType,
      },
      timestamp: new Date().toISOString(),
    });

    // Clear UTM params after conversion attribution
    sessionStorage.removeItem('utm_params');
  } catch (error) {
    console.error('Error tracking conversion with UTM:', error);
  }
}

/**
 * Map UTM medium to template name
 */
function mapUTMMediumToTemplateName(medium: string): string | null {
  const mapping: { [key: string]: string } = {
    'trial-welcome': 'trialWelcome',
    'trial-mid-check': 'trialMidPoint',
    'trial-ending': 'trialEnding',
  };
  return mapping[medium] || null;
}

/**
 * Initialize UTM tracking on page load
 */
export function initializeUTMTracking(userId?: string): void {
  // Extract UTM params from current URL
  const utmParams = extractUTMParams();

  if (utmParams) {
    // Store for later attribution
    storeUTMParams(utmParams);

    // Track click event if user is logged in
    if (userId) {
      trackUTMClick(userId, utmParams, window.location.href);
    }

    // Clean URL (remove UTM params for cleaner navigation)
    const url = new URL(window.location.href);
    url.searchParams.delete('utm_source');
    url.searchParams.delete('utm_medium');
    url.searchParams.delete('utm_campaign');
    url.searchParams.delete('utm_content');
    url.searchParams.delete('utm_term');

    // Update URL without reloading
    window.history.replaceState({}, '', url.toString());
  }
}
